import {
  formatUsd,
  formatDate as sharedFormatDate,
  formatDateTime as sharedFormatDateTime,
  formatRelativeTime,
} from "@excellent/ui/format"

// ─── Slug / Path helpers ─────────────────────────────────────────────────────

/**
 * Decode a project directory slug back to a filesystem path.
 * e.g. "-Users-foo-bar-myproject" → "/Users/foo/bar/myproject"
 */
export function slugToPath(slug: string): string {
  // Slugs start with a leading dash representing the root /
  return slug.replace(/-/g, '/')
}

/**
 * Encode a filesystem path to the slug format used by Claude Code.
 */
export function pathToSlug(path: string): string {
  return path.replace(/\//g, '-')
}

/**
 * Get a human-readable display name from a project path.
 * "/Users/foo/Developer/JavaScript/studio1" → "studio1"
 */
export function projectDisplayName(projectPath: string): string {
  if (!projectPath) return 'Unknown'
  const parts = projectPath.split(/[\\/]/)
  return parts[parts.length - 1] || parts[parts.length - 2] || projectPath
}

/**
 * Get a short display name (last 2 path segments) for longer context.
 */
export function projectShortPath(projectPath: string): string {
  if (!projectPath) return 'Unknown'
  const parts = projectPath.split(/[\\/]/).filter(Boolean)
  if (parts.length <= 2) return projectPath
  return `.../${parts.slice(-2).join('/')}`
}

// ─── Number formatters ───────────────────────────────────────────────────────

export function formatTokens(n: number): string {
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}B`
  if (n >= 1_000_000)     return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000)         return `${(n / 1_000).toFixed(1)}K`
  return String(n)
}

export function formatTokensExact(n: number): string {
  return n.toLocaleString()
}

// Money follows the app-wide standard: always 2 decimals via the shared
// formatter (no per-product precision). See
// docs/design-language/DISPLAY-CONSISTENCY-PRD.md §4.
export function formatCost(usd: number): string {
  return formatUsd(usd)
}

export function formatBytes(bytes: number): string {
  if (bytes >= 1_073_741_824) return `${(bytes / 1_073_741_824).toFixed(2)} GB`
  if (bytes >= 1_048_576)     return `${(bytes / 1_048_576).toFixed(2)} MB`
  if (bytes >= 1_024)         return `${(bytes / 1_024).toFixed(1)} KB`
  return `${bytes} B`
}

// ─── Duration formatters ─────────────────────────────────────────────────────

export function formatDuration(minutes: number): string {
  if (minutes < 1) return '<1m'
  if (minutes < 60) return `${Math.round(minutes)}m`
  const h = Math.floor(minutes / 60)
  const m = Math.round(minutes % 60)
  if (m === 0) return `${h}h`
  return `${h}h ${m}m`
}

export function formatDurationMs(ms: number): string {
  if (ms < 1000) return `${ms}ms`
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`
  return formatDuration(ms / 60_000)
}

// ─── Date formatters ─────────────────────────────────────────────────────────

// Dates follow the app-wide standard — delegate to the shared formatters so
// Lens renders dates exactly like the rest of the app (no seconds, consistent
// relative buckets). See docs/design-language/DISPLAY-CONSISTENCY-PRD.md §7.
export function formatRelativeDate(dateStr: string): string {
  return formatRelativeTime(dateStr)
}

export function formatDate(dateStr: string): string {
  return sharedFormatDate(dateStr)
}

export function formatDateTime(dateStr: string): string {
  return sharedFormatDateTime(dateStr)
}

export function formatTimestamp(ts: number): string {
  return sharedFormatDateTime(ts)
}

// ─── Percentage ──────────────────────────────────────────────────────────────

export function formatPct(value: number, total: number): string {
  if (total === 0) return '0%'
  return `${((value / total) * 100).toFixed(1)}%`
}
